import { Center, Flex, SimpleGrid, VisuallyHidden, Text } from "@chakra-ui/react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PaginationState, SortingState } from "@tanstack/react-table";
import { useQuery } from "@tanstack/react-query";
import { Alert, BodyShort } from "@digital-services-gard-as/anchor-react";
import { getExportedRejectedInvoices, InvoiceQueryFilter, RejectedQueryFilter } from "@/api/invoiceApi";
import { getRejectedInvoicesQueryOptions } from "@/api/queryOptions/invoiceAPIQueryOptions";
import { useSlowRequestToast } from "@/hooks/useSlowRequestToast";
import { Search } from "@/components/Search";

import { RejectedInvoicesTable } from "./Table";
import { DateRangePicker } from "../../../../../components/dateRangePicker/DateRangePicker";
import { DateRange } from "react-day-picker";
import { ExportTableList } from "../ExportTableList";

const QUERY_LIMIT = 10;
const DEFAULT_QUERY_FILTER: RejectedQueryFilter = {
    query: "",
    page: 0,
    limit: QUERY_LIMIT,
    sortDirection: "DESC",
    sortBy: "rejectedDate"
};
const formatDate = (date: Date) => {
    // converts the date to a string in the format YYYY-MM-DD,
    // and uses the locasl time instead of UTC to avoid timezone issues
    // where the date could be off by one day depending on the user's timezone

    const year = date.getFullYear();
    // eslint-disable-next-line no-magic-numbers
    const month = String(date.getMonth() + 1).padStart(2, "0");
    // eslint-disable-next-line no-magic-numbers
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
};

export const RejectedInvoiceTab = () => {
    const { t } = useTranslation(["claimsInvoicesOverview", "errorMessages"]);
    const [queryFilter, setQueryFilter] = useState<RejectedQueryFilter>({
        ...DEFAULT_QUERY_FILTER
    });
    const [searchText, setSearchText] = useState<string>("");
    const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
        pageIndex: DEFAULT_QUERY_FILTER.page,
        pageSize: DEFAULT_QUERY_FILTER.limit
    });
    const [sorting, setSorting] = useState<SortingState>([{ id: "rejectedDate", desc: true }]);
    const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);

    const {
        data: invoices,
        isFetching: isFetchingInvoices,
        isError: isInvoicesError
    } = useQuery(getRejectedInvoicesQueryOptions(queryFilter));

    useSlowRequestToast({
        isLoading: isFetchingInvoices
    });

    /**
     * Triggers invoice API requests whenever sorting state or pagination changes.
     * - Fires on changes to sorting, queryFilter, pageIndex, or pageSize.
     * - Calls getInvoices with the current filter, pagination, and sorting.
     * - Ensures the table updates when user changes sorting, page or rows per page.
     */
    useEffect(() => {
        let { sortBy, sortDirection } = queryFilter;

        if (sorting.length > 0) {
            sortBy = sorting[0].id;
            sortDirection = sorting[0].desc ? "DESC" : "ASC";
        }
        setQueryFilter({
            ...queryFilter,
            sortBy,
            sortDirection,
            limit: pageSize,
            page: pageIndex
        });
    }, [pageIndex, pageSize, sorting]);

    /**
     * Resets pagination to the initial state.
     */
    const resetPagination = useCallback(() => {
        setPagination({ pageSize: DEFAULT_QUERY_FILTER.limit, pageIndex: 0 });
    }, [setPagination]);

    /**
     * Reset pagination and fetch invoices on Enter key press.
     */
    const handleKeyUp = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "Enter") {
                resetPagination();
                if (queryFilter.query !== searchText) {
                    setQueryFilter({ ...queryFilter, query: searchText });
                }
            }
        },
        [queryFilter, resetPagination, searchText]
    );
    /**
     * Updates the query filter when the search text changes.
     */
    const handleSearchTextChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setSearchText(e.target.value);
        },
        [setSearchText]
    );
    /**
     * Triggers invoice API requests when the search button is clicked.
     */
    const handleSearchClick = useCallback(() => {
        resetPagination();
        if (queryFilter.query !== searchText) {
            setQueryFilter({ ...queryFilter, query: searchText });
        }
    }, [resetPagination, queryFilter, searchText]);
    /**
     * Triggers invoice API requests when the clear search button is clicked.
     */
    const handleClearSearch = useCallback(() => {
        resetPagination();
        setSearchText("");
        setQueryFilter({ ...queryFilter, query: "" });
    }, [queryFilter, resetPagination]);

    const handleDateRangeChange = useCallback(
        (range?: DateRange) => {
            setDateRange(range);
            resetPagination();
            setQueryFilter(prev => ({
                ...prev,
                rejectedFromDate: range?.from ? formatDate(range.from) : undefined,
                rejectedToDate: range?.to ? formatDate(range.to) : undefined,
                page: 0
            }));
        },
        [resetPagination]
    );

    const handleExportInvoices = useCallback(async (invoiceIds?: string[], exportQueryFilter?: InvoiceQueryFilter) => {
        await getExportedRejectedInvoices(invoiceIds, exportQueryFilter);
    }, []);

    /**
     * Triggers invoice API requests when the export button is clicked.
     */

    return (
        <Flex mt="1.5rem" gap="2rem" flexDir="column" w="100%">
            {isInvoicesError && (
                <Center>
                    <Alert id="rejectedInvoicesNoDataAlert" variant="danger" data-testid="rejectedInvoicesNoDataAlert">
                        <BodyShort>{t("errorMessages:claimsInvoicesOverview.cannotRetrieveData")}</BodyShort>
                    </Alert>
                </Center>
            )}
            {/* eslint-disable-next-line no-magic-numbers */}
            <SimpleGrid columns={{ base: 1, md: 3 }} gap={2} w="100%">
                <Flex
                    flexDirection={{ base: "column", lg: "row" }}
                    justifyContent={{ base: "flex-start", lg: "center" }}
                    alignItems="center"
                    gap="0.5rem"
                >
                    <Text whiteSpace="nowrap">{t("claimsInvoicesOverview:rejectedInvoicesTab.rejectedDateRange")}</Text>
                    <DateRangePicker value={dateRange} onChange={handleDateRangeChange} />
                </Flex>
                <Search
                    id="rejectedInvoices"
                    placeholder={t("claimsInvoicesOverview:rejectedInvoicesSearchPlaceholder")}
                    queryFilter={queryFilter}
                    searchText={searchText}
                    isFetchingData={isFetchingInvoices}
                    handleSearchCriteriaChange={handleSearchTextChange}
                    handleKeyUp={handleKeyUp}
                    handleSearchClick={handleSearchClick}
                    handleClearSearch={handleClearSearch}
                />
                <Flex justifyContent={{ base: "start", md: "end" }} gap="1rem" mr="1rem" alignContent="end">
                    {invoices && invoices?.items?.length > 0 ? (
                        <ExportTableList
                            invoiceIds={invoices.items.map(invoice => invoice.id)}
                            queryFilter={{ ...queryFilter, includedStatuses: ["Rejected"] }}
                            id="rejectedInvoices"
                            handleExport={handleExportInvoices}
                        />
                    ) : (
                        <VisuallyHidden />
                    )}
                </Flex>
            </SimpleGrid>

            <RejectedInvoicesTable
                invoicesResult={invoices}
                pageIndex={pageIndex}
                pageSize={pageSize}
                setPagination={setPagination}
                sorting={sorting}
                setSorting={setSorting}
                isFetchingInvoices={isFetchingInvoices}
            />
        </Flex>
    );
};
